import { XMarkIcon } from "@heroicons/react/24/outline";
import Avatar from "../shared/Avatar";
import classNames from "classnames";

/**
 * Compact client row for pickers and the selected-client summary.
 *
 * `highlighted` is only the dropdown hover or keyboard state. Client records
 * also have `active` (the account is in use, and new clients default to true).
 * Callers spread that record onto this component, so reading `active` paints
 * every client with the highlight background.
 *
 * @param {object} props
 * @param {string} [props.name]
 * @param {string} [props.slug]
 * @param {string} [props.avatar]
 * @param {boolean} [props.highlighted] Dropdown hover or keyboard highlight.
 * @param {boolean} [props.selected] Client currently chosen on the form.
 * @param {boolean} [props.disableCancel] Hide the remove control.
 * @param {Function} [props.onClick]
 * @param {Function} [props.onCancel]
 * @param {string} [props.className]
 */
export const AvatarClient = (props) => {
  const highlighted = Boolean(props.highlighted);

  const wrapperClasses = classNames(
    props.className,
    "flex items-center justify-between rounded-md py-2 px-2 w-full transition-colors",
    {
      "bg-primary-50 ring-1 ring-primary-100": highlighted,
      "hover:bg-gray-50": !highlighted && (props.onClick || !props.disableCancel),
      "cursor-pointer": Boolean(props.onClick),
    },
  );

  const nameClasses = classNames("text-sm", {
    "font-semibold text-primary-900": highlighted || props.selected,
    "font-medium text-gray-900": !highlighted && !props.selected,
  });

  const slugClasses = classNames("text-xs uppercase tracking-wide", {
    "text-primary-700/80": highlighted || props.selected,
    "text-gray-500": !highlighted && !props.selected,
  });

  return (
    <div
      className={wrapperClasses}
      onClick={props.onClick}
      onKeyDown={
        props.onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                props.onClick(event);
              }
            }
          : undefined
      }
      role={props.onClick ? "button" : undefined}
      tabIndex={props.onClick ? 0 : undefined}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Avatar size="xs" src={props.avatar} />
        <div className="min-w-0">
          <div className={nameClasses}>{props.name}</div>
          {props.slug ? (
            <div className={slugClasses}>{props.slug}</div>
          ) : null}
        </div>
      </div>
      {!props.disableCancel && props.selected && (
        <button
          type="button"
          aria-label="Remove client"
          className="ml-2 shrink-0 text-gray-400 hover:text-gray-600"
          onClick={(event) => {
            event.stopPropagation();
            props.onCancel?.(event);
          }}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
